'use client';

import { useEffect, useState } from 'react';
import { generateStringsFromRegex } from '@/lib/browser-mockdata-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserStringFromRegex() {
  const [pattern, setPattern] = useState('[A-Z]{3}-\\d{4}');
  const [count, setCount] = useState(10);
  const [output, setOutput] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function generate() {
    setCopyMessage(null);
    const result = generateStringsFromRegex(pattern, count);
    setError(result.error);
    setWarnings(result.warnings);
    setOutput(result.samples.join('\n'));
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Regex to string</span>
        <h2 className="tool-live-title">Generate strings from a regex</h2>
        <p className="tool-live-subtitle">
          Enter a regular expression and generate random strings that match it, locally in your
          browser. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Regex pattern</span>
              <input
                onChange={(event) => setPattern(event.target.value)}
                placeholder="[A-Z]{3}-\d{4}"
                type="text"
                value={pattern}
              />
            </label>
            <label className="tool-live-field">
              <span>How many strings</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(Number(event.target.value))}
                type="number"
                value={count}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={generate} type="button">
              Generate
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {warnings.length > 0 ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {warnings.join(' ')}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Matching strings</span>
            <textarea readOnly rows={16} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Supports literals, ., character classes, \d \w \s, quantifiers (* + ? {'{n,m}'}),
            groups, and alternation. Lookaround and backreferences are not supported.
          </p>
        </section>
      </div>
    </div>
  );
}
